import React, { useState } from 'react';
import Timer from './Timer';

interface EventHeaderProps {
  event: any;
  loading?: boolean;
  onRegisterClick?: () => void;
}

const EventHeader: React.FC<EventHeaderProps> = ({ event, loading = false, onRegisterClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getEventStatus = () => {
    if (event.setting.status === 'finished') {
      return <h5 className="mb-0">Event Ended</h5>;
    } else if (event.setting.status === 'postponed') {
      return <h5 className="text-warning mb-0">Postponed</h5>;
    } else if (event.setting.status === 'cancelled') {
      return <h5 className="text-warning mb-0">Cancelled</h5>;
    } else if (event.setting.status === 'sold_out' || event.setting.sold_out) {
      return (
        <h5 className="text-warning mb-0">
          {event.content.sold_out_text || 'Sold Out!'}
        </h5>
      );
    } else if (!event.setting.live) {
      return (
        <a href={`/pre-registration/${event.slug}`} className="btn btn-primary">
          Pre-Register
        </a>
      );
    }
    return null;
  };

  const scrollToCategories = () => {
    const categoriesElement = document.getElementById('event-pass-categories');
    if (categoriesElement) {
      categoriesElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleBannerClick = () => {
    setIsExpanded(false);
  };

        return (
          <section 
            className="event-header event-detail-header" 
            style={{ backgroundImage: `url('${event.content.banner_url}')` }}
          >
            <div className="header-content">
              <div className="container-xl">
                <div 
                  className={`event-info-container mt-3 ${isExpanded ? 'expanded' : ''}`}
                  onClick={isExpanded ? handleBannerClick : undefined}
                  style={isExpanded ? { '--banner-url': `url('${event.content.banner_url}')` } as React.CSSProperties : undefined}
                >
                  <div className="event-thumbnail">
                    {event.content.header_video_url ? (
                      <div 
                        className="jarallax"
                        data-jarallax
                        data-video-src={event.content.header_video_url}
                        style={{ height: '100%' }}
                      >
                      </div>
                    ) : (
                      <div className="thumbnail-wrapper" onClick={handleThumbnailClick}>
                        {event.content.thumbnail_url ? (
                          <img 
                            src={event.content.thumbnail_url}
                            alt={event.title}
                            className="thumbnail-image"
                            loading="eager"
                            decoding="async"
                            onLoad={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.opacity = '1';
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              // Fallback to banner if thumbnail fails
                              if (event.content.banner_url && target.src !== event.content.banner_url) {
                                target.src = event.content.banner_url;
                              } else {
                                target.style.display = 'none';
                              }
                            }}
                            style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                          />
                        ) : event.content.banner_url ? (
                          <img 
                            src={event.content.banner_url}
                            alt={event.title}
                            className="thumbnail-image"
                            loading="eager"
                            decoding="async"
                            onLoad={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.opacity = '1';
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                            style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                          />
                        ) : null}
                        {event.content.banner_url && event.content.thumbnail_url && (
                          <img 
                            src={event.content.banner_url}
                            alt={`${event.title} Banner`}
                            className="banner-image"
                            loading="eager"
                            decoding="async"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="expand-overlay">
                          <i className="fas fa-expand-arrows-alt"></i>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="event-details" onClick={(e) => e.stopPropagation()}>
                    <div className="event-title">
                      <div className="title-content">
                        <h1>{event.title}</h1>
                      </div>
                    </div>
                    
                    <div className="event-meta">
                      <div className="meta-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{event.venue.name}</span>
                      </div>
                      <div className="meta-item">
                        <i className="fas fa-calendar-alt"></i>
                        <span>{formatDate(event.starts)}</span>
                      </div>
                      <div className="meta-item">
                        <i className="fas fa-clock"></i>
                        <span>{formatTime(event.starts)}</span>
                      </div>
                    </div>

                    <div className="action-container">
                      <div className="timer-section">
                        <Timer event={event} />
                      </div>
                      
                      <div className="button-section">
                        {event.setting.live ? (
                          <a 
                            href="#event-pass-categories"
                            className="register-button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (onRegisterClick) {
                                onRegisterClick();
                              } else {
                                scrollToCategories();
                              }
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 146.39 40.51">
                              <defs>
                                <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" style={{ stopColor: '#F23F39', stopOpacity: 1 }} />
                                  <stop offset="100%" style={{ stopColor: '#f45e27', stopOpacity: 1 }} />
                                </linearGradient>
                              </defs>
                              <g id="Layer_2" data-name="Layer 2">
                                <g id="Layer_1-2" data-name="Layer 1">
                                  <path 
                                    fill="url(#grad1)"
                                    d="M141.31,20.26a6.24,6.24,0,0,1,5.08-6V.75a.77.77,0,0,0-.78-.75H131.75a.71.71,0,0,0-.6.26,3,3,0,0,1-2.48,1.3A2.94,2.94,0,0,1,126.2.27a.74.74,0,0,0-.61-.27H20.81a.73.73,0,0,0-.61.26,2.91,2.91,0,0,1-2.14,1.28h0l-.45,0A3,3,0,0,1,15.25.27a.72.72,0,0,0-.6-.27H.78A.77.77,0,0,0,0,.75V14.22a6.24,6.24,0,0,1,5.08,6A6.25,6.25,0,0,1,0,26.3V39.76a.77.77,0,0,0,.78.75H14.65a.74.74,0,0,0,.6-.26,3,3,0,0,1,4.95,0,.71.71,0,0,0,.61.27H125.59a.72.72,0,0,0,.6-.26A3,3,0,0,1,128.34,39l.25,0h.19a3,3,0,0,1,2.36,1.31.72.72,0,0,0,.61.27h13.86a.77.77,0,0,0,.78-.75V26.3A6.25,6.25,0,0,1,141.31,20.26Z"
                                  />
                                </g>
                              </g>
                            </svg>
                            <h6 className="text-center">{event.content.button_label}</h6>
                          </a>
                        ) : (
                          getEventStatus()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex justify-content-center justify-content-md-end">
                  <div className="share-buttons d-flex mt-3">
                    <a 
                      href={`/event-reminder/${event.slug}`} 
                      className="share-button"
                      target="_blank" 
                      title="Add to Calendar"
                    >
                      <i className="fas fa-calendar-plus"></i>
                    </a>
                    <a 
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      className="share-button"
                      target="_blank"
                      title="Share on Facebook"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a 
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(event.title)}&url=${encodeURIComponent(window.location.href)}`}
                      className="share-button"
                      target="_blank"
                      title="Share on Twitter"
                    >
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a 
                      href={`mailto:?subject=${encodeURIComponent(`New Event: ${event.title}`)}&body=${encodeURIComponent(`Check out this event: ${window.location.href}`)}`}
                      className="share-button"
                      title="Send Email"
                    >
                      <i className="fas fa-envelope"></i>
                    </a>
                    <button 
                      className="share-button"
                      onClick={() => navigator.clipboard.writeText(window.location.href)}
                      title="Copy Link"
                    >
                      <i className="fas fa-link"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
};

export default EventHeader;
